import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { Pencil, Check, Trash2, ExternalLink, Image, Variable, Plus, Upload, Loader } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import { uploadDocumentFile } from "../../apis/documents";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

const MIN_CARDS = 2;
const MAX_CARDS = 10;
const MAX_BODY_LEN = 1024;
const MAX_HEADER_LEN = 60;
const MAX_URL_LABEL_LEN = 20;

const emptyCard = () => ({
  id: `card_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  header: { format: "IMAGE", mediaUrl: "" },
  body: "",
  urlLabel: "Visit",
  url: "",
});

/* ── Collapsed View ───────────────────────────────────────────── */
const CollapsedView = ({ data, onEdit }) => {
  const cards = data.cards || [];
  const cardsSource = data.cardsSource || "manual";

  const bodyText = data.body?.text || "";

  return (
    <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: "#EFEAE2", padding: "10px 10px 6px", minWidth: "280px", maxWidth: "340px" }}>
      <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg" title="Edit">
        <Pencil size={12} className="text-teal-600" />
      </button>

      {bodyText && <p className="text-[10px] text-[#111B21] mb-1.5 truncate">{bodyText}</p>}

      {cardsSource === "manual" && cards.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "thin" }}>
          {cards.map((card, i) => (
            <div key={card.id || i} className="bg-white rounded-lg shadow-sm flex-shrink-0" style={{ width: 180 }}>
              {card.header?.mediaUrl && !card.header.mediaUrl.includes("{{") ? (
                card.header?.format === "VIDEO"
                  ? <video src={card.header.mediaUrl} className="w-full h-16 object-cover rounded-t-lg" muted />
                  : <img src={card.header.mediaUrl} alt="" className="w-full h-16 object-cover rounded-t-lg" />
              ) : (
                <div className="w-full h-16 bg-[#F0F0F0] rounded-t-lg flex items-center justify-center"><Image size={16} className="text-gray-300" /></div>
              )}
              <div className="p-2">
                <p className="text-[9px] text-[#111B21] truncate">{card.body || "Card body..."}</p>
                <div className="border-t border-gray-100 mt-1 pt-1 flex items-center justify-center gap-1">
                  <ExternalLink size={9} className="text-[#027EB5]" />
                  <span className="text-[9px] text-[#027EB5] font-medium truncate">{card.urlLabel || "Visit"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : cardsSource === "variable" ? (
        <div className="bg-white rounded-lg shadow-sm p-3 text-center">
          <Variable size={16} className="text-teal-600 mx-auto mb-1" />
          <p className="text-[10px] text-teal-600 font-semibold">Cards from variable</p>
          <p className="text-[8px] text-[#8696a0]">{data.cardsVariable ? `{{${data.cardsVariable}}}` : "Not configured"}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-3 text-center">
          <p className="text-[10px] text-gray-400">No cards added (min {MIN_CARDS})</p>
        </div>
      )}
    </div>
  );
};

/* ── Expanded Editor ──────────────────────────────────────────── */
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState(() => ({
    body: data.body?.text ?? "",
    cardsSource: data.cardsSource || "manual",
    cards: data.cards ? JSON.parse(JSON.stringify(data.cards)) : [emptyCard(), emptyCard()],
    cardsVariable: data.cardsVariable || "",
    cardFieldMap: { type: "", body: "", mediaUrl: "", urlLabel: "", url: "", ...(data.cardFieldMap || {}) },
    selectionVariable: data.selectionVariable || "",
  }));

  const [saveError, setSaveError] = useState(null);

  const handleSave = () => {
    const trimmed = form.body.trim();
    if (!trimmed) { setSaveError("Carousel message is required"); return; }
    if (form.cardsSource === "manual") {
      if (form.cards.length < MIN_CARDS) { setSaveError(`At least ${MIN_CARDS} cards required`); return; }
      for (let i = 0; i < form.cards.length; i++) {
        const hdr = form.cards[i].header;
        if (!["IMAGE","VIDEO"].includes(hdr?.format)) { setSaveError(`Card ${i + 1}: header must be Image or Video`); return; }
        if (!(hdr?.mediaUrl || "").trim()) { setSaveError(`Card ${i + 1}: header media URL is required`); return; }
      }
    }
    setSaveError(null);
    const { body, ...rest } = form;
    const cardFieldMap = { ...(data.cardFieldMap || {}), ...rest.cardFieldMap };
    onSave({ ...rest, cardFieldMap, body: { text: trimmed } });
  };

  const updateCards = (cards) => setForm(prev => ({ ...prev, cards }));

  const addCard = () => {
    if (form.cards.length >= MAX_CARDS) return;
    updateCards([...form.cards, emptyCard()]);
  };

  const removeCard = (idx) => {
    if (form.cards.length <= MIN_CARDS) return;
    updateCards(form.cards.filter((_, i) => i !== idx));
  };

  const updateCard = (idx, field, value) => {
    const cards = JSON.parse(JSON.stringify(form.cards));
    if (field.includes(".")) {
      const [p1, p2] = field.split(".");
      cards[idx][p1] = { ...(cards[idx][p1] || {}), [p2]: value };
    } else {
      cards[idx][field] = value;
    }
    updateCards(cards);
  };

  const fileInputRefs = useRef({});
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const handleMediaUpload = async (cardIdx, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingIdx(cardIdx);
    try {
      const response = await uploadDocumentFile(file);
      updateCard(cardIdx, "header.mediaUrl", response.data.data.doc_url);
    } catch { /* ignore */ }
    finally {
      setUploadingIdx(null);
      if (fileInputRefs.current[cardIdx]) fileInputRefs.current[cardIdx].value = "";
    }
  };

  return (
    <div className="nowheel" style={{ backgroundColor: "#EFEAE2", borderRadius: "12px", padding: "14px", minWidth: "400px", maxWidth: "460px" }} onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-teal-500" /><span className="text-[13px] font-bold text-[#111B21]">Carousel URL</span></div>
        <div className="flex gap-1.5">
          <button onClick={handleSave} className="px-3 py-1.5 text-[11px] font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1 shadow-sm"><Check size={12} />Save</button>
          <button onClick={onCancel} className="px-3 py-1.5 text-[11px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
        </div>
      </div>

      <NodeEditorTabs nodeType="interactiveCarouselUrl">
      {/* Carousel message (body) */}
      <div className="mb-3">
        <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">Carousel Message <span className="text-red-500">*</span></label>
        <input value={form.body} onChange={e => { setForm(p => ({ ...p, body: e.target.value })); setSaveError(null); }} placeholder="Check out our offers" maxLength={MAX_BODY_LEN} className={`w-full px-2 py-1.5 text-[10px] bg-[#F0F0F0] rounded border focus:outline-none placeholder:text-[#8696a0] ${saveError ? "border-red-500" : "border-transparent"}`} />
        {saveError && <p className="text-[9px] text-red-500 mt-0.5">{saveError}</p>}
      </div>

      {/* Source toggle */}
      <div className="flex gap-2 mb-3">
        <button onClick={() => setForm(p => ({ ...p, cardsSource: "manual" }))} className={`flex-1 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${form.cardsSource === "manual" ? "bg-teal-600 text-white" : "bg-white text-[#667781] border border-gray-200"}`}>Manual Cards</button>
        <button onClick={() => setForm(p => ({ ...p, cardsSource: "variable" }))} className={`flex-1 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${form.cardsSource === "variable" ? "bg-teal-600 text-white" : "bg-white text-[#667781] border border-gray-200"}`}>From Variable</button>
      </div>

      {form.cardsSource === "manual" && (
        <div className="space-y-2 mb-3 max-h-[400px] overflow-y-auto pr-1">
          {form.cards.map((card, i) => (
            <div key={card.id || i} className="bg-white rounded-lg shadow-sm p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold text-teal-600">Card {i + 1}</span>
                <button onClick={() => removeCard(i)} disabled={form.cards.length <= MIN_CARDS} className="p-0.5 text-[#667781] hover:text-red-500 disabled:opacity-30"><Trash2 size={10} /></button>
              </div>
              <select value={card.header?.format || "IMAGE"} onChange={e => updateCard(i, "header.format", e.target.value)} className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded mb-1 border-0 focus:outline-none">
                <option value="IMAGE">Image Header</option><option value="VIDEO">Video Header</option>
              </select>
              <div className="flex gap-1 items-center mb-1">
                <input value={card.header?.mediaUrl || ""} onChange={e => updateCard(i, "header.mediaUrl", e.target.value)} placeholder={`${card.header?.format === "VIDEO" ? "Video" : "Image"} URL or {{variable}}`} className="flex-1 px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none placeholder:text-[#8696a0] font-mono" />
                <label className="cursor-pointer flex-shrink-0">
                  {uploadingIdx === i
                    ? <span className="flex items-center gap-1 px-2 py-1 bg-teal-50 rounded"><Loader size={10} className="text-teal-600 animate-spin" /><span className="text-[8px] text-teal-600">...</span></span>
                    : <span className="flex items-center gap-1 px-2 py-1 bg-[#F0F0F0] hover:bg-[#E8E8E8] rounded transition-colors"><Upload size={10} className="text-[#667781]" /><span className="text-[8px] text-[#667781]">Upload</span></span>}
                  <input ref={el => fileInputRefs.current[i] = el} type="file" className="hidden" accept={card.header?.format === "VIDEO" ? "video/*" : "image/*"} onChange={e => handleMediaUpload(i, e)} disabled={uploadingIdx !== null} />
                </label>
              </div>
              <textarea value={card.body || ""} onChange={e => updateCard(i, "body", e.target.value)} placeholder="Card body" rows={2} maxLength={MAX_BODY_LEN} className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded mb-1 border-0 focus:outline-none resize-none placeholder:text-[#8696a0]" />
              <div className="flex gap-1">
                <input value={card.urlLabel || ""} onChange={e => updateCard(i, "urlLabel", e.target.value)} placeholder="Button label" maxLength={MAX_URL_LABEL_LEN} className="w-1/3 px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none placeholder:text-[#8696a0]" />
                <input value={card.url || ""} onChange={e => updateCard(i, "url", e.target.value)} placeholder="https://... or {{var}}" className="flex-1 px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none placeholder:text-[#8696a0] font-mono" />
              </div>
            </div>
          ))}
          <button onClick={addCard} disabled={form.cards.length >= MAX_CARDS} className="text-[10px] text-teal-600 font-bold hover:underline disabled:opacity-30 flex items-center gap-1"><Plus size={10} />Add Card</button>
          <p className="text-[8px] text-[#8696a0]">Cards: {form.cards.length}/{MAX_CARDS} (min {MIN_CARDS})</p>
        </div>
      )}

      {form.cardsSource === "variable" && (
        <div className="mb-3 bg-white rounded-lg shadow-sm p-2.5 space-y-2">
          <label className="text-[10px] font-bold text-[#667781] uppercase">Array Variable</label>
          <VariableDropdown value={form.cardsVariable} onChange={name => setForm(p => ({ ...p, cardsVariable: name }))} flowVariables={flowVariables||[]} allowCreate onCreateVariable={onCreateVariable} placeholder="Select array variable..." />
          <p className="text-[8px] text-[#8696a0]">Each card: type (image or video), body, media URL, and URL button (label + url).</p>
          <p className="text-[8px] text-[#8696a0]">Map array element fields:</p>
          {[
            { key: "type", label: "Type (image/video)" },
            { key: "body", label: "Body" },
            { key: "mediaUrl", label: "Media" },
            { key: "urlLabel", label: "URL Label" },
            { key: "url", label: "URL" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-[9px] font-semibold text-[#667781] w-28">{label}</span>
              <input value={form.cardFieldMap[key] || ""} onChange={e => setForm(p => ({ ...p, cardFieldMap: { ...p.cardFieldMap, [key]: e.target.value } }))} placeholder={`e.g. ${key}`} className="flex-1 px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/30" />
            </div>
          ))}
        </div>
      )}

      {/* Store selection */}
      <div className="mb-3">
        <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">Store selection in variable</label>
        <VariableDropdown value={form.selectionVariable || ""} onChange={name => setForm(p => ({ ...p, selectionVariable: name }))} flowVariables={flowVariables||[]} allowCreate onCreateVariable={onCreateVariable} placeholder="Optional..." />
      </div>
      </NodeEditorTabs>

      <div className="flex gap-2">
        <button onClick={handleSave} className="flex-1 py-2 text-[12px] font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"><Check size={14} />Save</button>
        <button onClick={onCancel} className="flex-1 py-2 text-[12px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
      </div>
    </div>
  );
};

const InteractiveCarouselUrlNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("interactiveCarouselUrl");
  const handleSave = useCallback((formData) => {
    if (data.onNodeDataChange) data.onNodeDataChange(id, { ...data, ...formData });
    closeEditor();
  }, [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle type="target" position={Position.Left} className="!w-3.5 !h-3.5 !bg-teal-500 !border-2 !border-white !shadow-sm" style={{ left: -7 }} />
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          presentation="fullscreen"
          onCancel={closeEditor}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
        />
      </FlowNodeEditorShell>
      <Handle type="source" position={Position.Right} id="default" className="!w-3.5 !h-3.5 !bg-teal-500 !border-2 !border-white !shadow-sm" style={{ right: -7 }} />
    </div>
  );
};

export default InteractiveCarouselUrlNode;
